import { useResolvedEditorVersion } from '@/hooks/editor-version/useResolvedEditorVersion';
import { useNoteEditorFlags } from '@/hooks/useNoteEditorFlags';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  heading: {
    defaultMessage: 'Editor version',
    id: 'components.dev.editorVersion.heading',
  },
  description: {
    defaultMessage:
      'The editor the backend resolved for this user (template_editor_version on the latest completed Athena onboarding session) and the note-editor flags it produces.',
    id: 'components.dev.editorVersion.description',
  },
  resolvedLabel: {
    defaultMessage: 'Resolved version',
    id: 'components.dev.editorVersion.resolvedLabel',
  },
  sourceLabel: {
    defaultMessage: 'Source',
    id: 'components.dev.editorVersion.sourceLabel',
  },
  authoritativeLabel: {
    defaultMessage: 'Backend sent a concrete decision',
    id: 'components.dev.editorVersion.authoritativeLabel',
  },
  settledLabel: {
    defaultMessage: 'Settled',
    id: 'components.dev.editorVersion.settledLabel',
  },
  flagsHeading: {
    defaultMessage: 'Resulting note-editor flags',
    id: 'components.dev.editorVersion.flagsHeading',
  },
  markdownNativeLabel: {
    defaultMessage: 'markdown-native',
    id: 'components.dev.editorVersion.markdownNativeLabel',
  },
  blockV1Label: {
    defaultMessage: 'block editor v1 (HTML)',
    id: 'components.dev.editorVersion.blockV1Label',
  },
  blockMarkdownLabel: {
    defaultMessage: 'block editor markdown',
    id: 'components.dev.editorVersion.blockMarkdownLabel',
  },
  richTextLabel: {
    defaultMessage: 'rich text',
    id: 'components.dev.editorVersion.richTextLabel',
  },
  flagsOverridden: {
    defaultMessage:
      'A concrete backend decision is in force, so these flags are overridden by it — no client feature flag gets a vote.',
    id: 'components.dev.editorVersion.flagsOverridden',
  },
  flagsPassthrough: {
    defaultMessage:
      'No concrete backend decision, so the legacy client flags decide (pre-#18956 behaviour).',
    id: 'components.dev.editorVersion.flagsPassthrough',
  },
  jsonNoteCaveat: {
    defaultMessage:
      'A completed JSON note still renders the JSON editor regardless of this value — stored note content cannot be re-rendered as markdown. The resolved version governs how NEW notes are generated.',
    id: 'components.dev.editorVersion.jsonNoteCaveat',
  },
  yes: {
    defaultMessage: 'yes',
    id: 'components.dev.editorVersion.yes',
  },
  no: {
    defaultMessage: 'no',
    id: 'components.dev.editorVersion.no',
  },
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <TypographyCaption className="text-text-secondary">{label}</TypographyCaption>
      <TypographyCaption className="shrink-0 font-mono text-text-primary">
        {value}
      </TypographyCaption>
    </div>
  );
}

/**
 * The staging/dev read-out of the editor determination.
 *
 * Operators debugging a migration flip need to see whether the backend's
 * decision actually reached this client — the resolved value alone is not
 * enough, because a fail-closed `html` and a genuine `html` resolution look
 * identical. `isAuthoritative` is the discriminator, and the flag block below
 * shows which editor the decision actually produces.
 */
export function EditorVersionDevSection() {
  const intl = useIntl();
  const { version, source, isSettled, isAuthoritative } = useResolvedEditorVersion();
  const flags = useNoteEditorFlags();
  const bool = (value: boolean | undefined) =>
    intl.formatMessage(value ? messages.yes : messages.no);

  return (
    <section aria-labelledby="dev-tools-editor-version-heading">
      <TypographyP2SemiBold id="dev-tools-editor-version-heading" className="mb-2">
        {intl.formatMessage(messages.heading)}
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-2 block text-text-tertiary">
        {intl.formatMessage(messages.description)}
      </TypographyCaption>

      <div className="space-y-1">
        <Row label={intl.formatMessage(messages.resolvedLabel)} value={version} />
        <Row label={intl.formatMessage(messages.sourceLabel)} value={source} />
        <Row
          label={intl.formatMessage(messages.authoritativeLabel)}
          value={bool(isAuthoritative)}
        />
        <Row label={intl.formatMessage(messages.settledLabel)} value={bool(isSettled)} />
      </div>

      <TypographyCaption className="mb-1 mt-3 block text-text-secondary">
        {intl.formatMessage(messages.flagsHeading)}
      </TypographyCaption>
      <div className="space-y-1">
        <Row
          label={intl.formatMessage(messages.markdownNativeLabel)}
          value={bool(flags.isMarkdownNativeEditorEnabled)}
        />
        <Row
          label={intl.formatMessage(messages.blockV1Label)}
          value={bool(flags.enableBlockEditorV1)}
        />
        <Row
          label={intl.formatMessage(messages.blockMarkdownLabel)}
          value={bool(flags.enableBlockEditorMarkdown)}
        />
        <Row
          label={intl.formatMessage(messages.richTextLabel)}
          value={bool(flags.richTextEditorEnabled)}
        />
      </div>

      <TypographyCaption className="mt-2 block text-text-tertiary">
        {intl.formatMessage(
          isAuthoritative ? messages.flagsOverridden : messages.flagsPassthrough
        )}
      </TypographyCaption>
      <TypographyCaption className="mt-2 block text-text-tertiary">
        {intl.formatMessage(messages.jsonNoteCaveat)}
      </TypographyCaption>
    </section>
  );
}
